import React from 'react'
import type { DownloadPopoverProps } from '../FloatingInspectorButton/SharedComponents/Popover/DownloadPopover/DownloadPopover'

/**
 * Minimal data contract for a thumbnail item. Enables SceneItem and
 * ScenesDraggingWrapper to operate with non-Scene data sources (e.g.
 * AI-result grids) via SceneItemConfig render-function overloads.
 */
export interface ThumbnailItemData {
  id: string
  preview?: string
  /** When provided, a muted looping video element is shown instead of a static image. */
  videoUrl?: string
  isLoading?: boolean
  /** Shows the GradientLoader as an overlay on top of the existing image/video. */
  loadingOverlay?: boolean
  isDisabled?: boolean
  /** Per-item thumbnail size. When provided, SceneItem uses it as both baseSize and activeSize. */
  size?: { width: number; height: number }
}

/**
 * Content of a thumbnail overlay slot.
 * - `'orderBadge'`: scene order number
 * - `'downloadButton'`: download badge + popover (configure via `downloadPopoverProps`)
 * - function: custom renderer; receives minimal item data and 1-based order index
 */
export type SceneSlotElement =
  | 'orderBadge'
  | 'downloadButton'
  | ((item: ThumbnailItemData, order: number) => React.ReactNode)

/** A slot element, optionally shown only while the item is revealed (hovered / popover open). */
export type SceneSlot = SceneSlotElement | { element: SceneSlotElement; revealOnHover?: boolean }

export interface SceneItemConfig {
  /** Bottom-left overlay slot. */
  bottomLeftElement: SceneSlot

  /** Bottom-right overlay slot. Empty by default. */
  bottomRightElement?: SceneSlot

  /**
   * Per-item options merged into the built-in `'downloadButton'` popover
   * (e.g. `onSave`, `defaultFormatKey`). Return `null` to hide the button for
   * that item. Defaults to exporting the scene by id.
   */
  downloadPopoverProps?: (item: ThumbnailItemData) => Partial<DownloadPopoverProps> | null

  /**
   * Render the three-dot context menu.
   * - `'pageMenu'`: standard EditPageMenu
   * - `'stateMenu'`: DownloadStateMenu
   * - function: custom renderer; receives item data and a close callback
   * - omitted: no three-dot menu (right-click does nothing)
   */
  popover?:
    | 'pageMenu'
    | 'stateMenu'
    | ((item: ThumbnailItemData, onClose: () => void) => React.ReactNode)

  /**
   * Replaces the default image/video thumbnail content — for sources whose
   * preview is a live composition rather than a flat image (e.g. Batch).
   * Receives the item and its rendered thumbnail size.
   */
  renderPreview?: (item: ThumbnailItemData, size: { width: number; height: number }) => React.ReactNode

  /** When false, drag-and-drop is disabled for items. Defaults to true. */
  draggable?: boolean

  /** Accessible name for an item; receives its 1-based order index. */
  getItemLabel?: (item: ThumbnailItemData, order: number) => string

  autoSelectActiveObjectWhenChangingScene?: boolean
  focusActiveObjectWhenChangingScene?: boolean
}

export const scenesConfig: SceneItemConfig = {
  bottomLeftElement: 'orderBadge',
  popover: 'pageMenu',
  draggable: true,
}

export const downloadConfig: SceneItemConfig = {
  bottomLeftElement: 'downloadButton',
  popover: 'stateMenu',
  draggable: true,
  autoSelectActiveObjectWhenChangingScene: true,
  focusActiveObjectWhenChangingScene: true,
}

/**
 * Props passed to Scenes / ScenesProvider when rendering an externally-managed
 * item list (e.g. the AI Studio results panel). The provider skips all
 * editor event listeners and routes everything through these callbacks.
 */
export interface ScenesExternalDataProps {
  items: ThumbnailItemData[]
  selectedIds: string[]
  config: SceneItemConfig
  /** Whether the panel is "active" (focused). Defaults to true in external mode. */
  isActive?: boolean
  onSelect: (item: ThumbnailItemData, event: React.MouseEvent, isTargetTriggerPopover: boolean) => void
  onDownloadBadgeClick?: (item: ThumbnailItemData) => void
  onDownloadPopoverOpenChange?: (itemId: string, isOpen: boolean) => void
  openDownloadPopoverItemId?: string | null
  onDragStart?: (id: string) => void
  onDragEnd?: (activeId: string, overId: string, moveBackwards: boolean) => void
  /**
   * Keep the first selected item scrolled into view when selection changes from
   * outside the list (e.g. keyboard navigation of a stage the list mirrors).
   */
  scrollSelectedIntoView?: boolean
  /** Called with the item id and the native image load event for natural-size tracking. */
  onImageLoad?: (itemId: string, event: React.SyntheticEvent<HTMLImageElement>) => void
  /** Called with the item id and the video loadedmetadata event for natural-size tracking. */
  onVideoLoad?: (itemId: string, event: React.SyntheticEvent<HTMLVideoElement>) => void
}
